import { useCallback, useEffect, useState } from 'react'
import { EnvVariable } from '../../models/Environment'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Alert, Badge, Breadcrumb, Button, Container, Modal, Card } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { EnvironmentVariables } from '../../api/EnvironmentVariables';
import { useToasts } from '../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import AlidaLoader from '../../components/common/AlidaLoader';
import { Col, Flex, Tag, Avatar } from 'antd';
import { useAuth } from '../../contexts/auth';
import UserItem from '../../components/common/UserItem';
import ScriptEditor from '../../components/common/ScriptEditor';
import EnvironmentVariableForm from '../../components/forms/EnvironmentVariableForm';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';


export default function EnvironmentVariableDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [variable, setVariable] = useState<EnvVariable>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();
  const { showToast } = useToasts();

  const variableLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));

  const initVariable = useCallback(() => {
    EnvironmentVariables.getEnvironmentVariable(id as string)
      .then(
        ({ data }) => setVariable(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('variable-error', message, `${variableLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast, variableLabels]);

  useEffect(() => {
    if (id) initVariable();
  }, [id, initVariable]);

  const reset = useCallback(() => {
    setError(false);
    setVariable(undefined);
    initVariable();
  }, [initVariable]);

  const handleDelete = useCallback(() => setShowDeleteModal(true), []);
  const handleEdit = useCallback(() => setShowEditModal(true), []);

  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setShowEditModal(false);
  }, []);

  const deleteVariable = useCallback(() => {
    showLoader();

    EnvironmentVariables.deleteEnvironmentVariable(variable!.id!)
      .then(
        () => {
          hideLoader();
          handleClose();
          showToast('environment-variable-success', `${variableLabels.singular} deleted successfully`, `${variableLabels.singular} Success`, 'success');
          navigate('/environment-variables');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('environment-variable-error', message, `${variableLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, variable, showToast, navigate, variableLabels]);

  const handleSubmit = useCallback((values: EnvVariable) => {
    showLoader();

    EnvironmentVariables.updateEnvironmentVariable(variable!.id!, values)
      .then(
        () => {
          hideLoader();
          showToast('environment-variable-success', `${variableLabels.singular} updated successfully`, `${variableLabels.singular} Updated`, 'success');
          handleClose();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('environment-variable-error', message, `${variableLabels.singular} ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [handleClose, reset, showLoader, hideLoader, showToast, variable, variableLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred while retrieving {variableLabels.singular}. Try again later.</span>
      </Alert>
    );

    if (!variable) return (
      <Flex justify="center" className="py-5">
        <AlidaLoader size={300} />
      </Flex>
    );

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/environment-variables' }}>{variableLabels.plural}</Breadcrumb.Item>
          <Breadcrumb.Item active>{variable.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card>
          <Flex justify="flex-end" className="p-1">
            <Badge>{variable.accessLevel}</Badge>
          </Flex>

          <Card.Body className="pt-0">
            <Flex align="center" className="mb-2">
              <Col span={3} className="text-center pe-2">
                <Avatar
                  src={variable.img}
                  icon={<FontAwesomeIcon icon={solid('camera')} size="xl" />}
                  className="border"
                  style={{ height: 80, width: 80 }}
                />
              </Col>
              <Col flex="auto">
                <h5 className="mb-1">{variable.name}</h5>
                <span className="text-muted">{variable.description}</span>
              </Col>
              <Col span={5} className="text-end">
                {variable.owner?.id === loggedUser?.sub && (
                  <Flex className="gap-2" align="center" justify="flex-end">
                    <button className="circle-button dark ms-2" onClick={handleEdit}>
                      <FontAwesomeIcon icon={solid('pen')} />
                    </button>
                    <button className="circle-button dark" onClick={handleDelete}>
                      <FontAwesomeIcon icon={solid('trash')} />
                    </button>
                  </Flex>
                )}
              </Col>
            </Flex>

            <table className="w-75 mx-auto table-transparent">
              <thead>
                <th></th>
                <th style={{ width: "50%" }}></th>
              </thead>
              <tbody>
                <tr>
                  <td></td>
                  <td className="text-end">
                    <div className="border rounded py-1 px-3">
                      <UserItem className="text-start" organization={variable.organization} user={variable.owner} />
                    </div>
                  </td>
                </tr>
                <tr>
                  <th className="align-top" scope="col">Type</th>
                  <td className="text-end">
                    <Tag color="geekblue">{variable.type}</Tag>
                  </td>
                </tr>
                <tr>
                  <th className="align-top" scope="col">Value</th>
                  <td className="text-end">
                    {variable.type === 'json'
                      ? <ScriptEditor readOnly value={variable.value} height={300} />
                      : <code>{variable.value}</code>
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </Card.Body>
        </Card>
      </>
    )
  }, [variable, error, handleDelete, handleEdit, loggedUser, variableLabels]);

  return (
    <Container className="my-3">
      <Helmet>
        <title>{variableLabels.singular} {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit {variableLabels.singular}</Modal.Title>
        </Modal.Header>
        <EnvironmentVariableForm environmentVariable={variable} onSubmit={handleSubmit} onCancel={handleClose} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {variableLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {variableLabels.singular} <b>{variable?.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteVariable}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}